import React, { useState, useEffect, useCallback } from "react";
import Button from "../Button";
import SidebarItem from "./SidebarItem";
import { IoLogOut, IoPerson, IoWallet } from "react-icons/io5";
import { LuPanelLeftClose, LuPanelLeftOpen } from "react-icons/lu";
import { HiOutlineUserCircle } from "react-icons/hi2";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";

const STORAGE_KEY = "sidebar-collapsed";

function getInitials(name) {
  if (!name || name === "------") return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function NotificationTernaryItem({
  Icon,
  title,
  isCollapsed,
  NotificationLayout,
  tooltipSide,
}) {
  const trigger = (
    <div
      className={`text-background/70 hover:bg-primary/10 border-background/30 flex group items-center rounded-xl border-[2px] transition-colors ease-in-out duration-150 ${
        isCollapsed
          ? "w-10 h-10 justify-center p-0"
          : "flex-1 justify-center p-2.5"
      }`}
    >
      {Icon && (
        <Icon className="text-xl group-hover:text-primary/80 shrink-0" />
      )}
    </div>
  );

  return (
    <Sheet>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild>{trigger}</SheetTrigger>
        </TooltipTrigger>
        <TooltipContent
          side={tooltipSide}
          align="center"
          className="z-50"
          sideOffset={isCollapsed ? 8 : 4}
        >
          {title}
        </TooltipContent>
      </Tooltip>
      <SheetContent
        className="bg-background_2 w-full !max-w-none lg:w-[520px]"
        aria-describedby=""
      >
        <SheetHeader>
          <SheetTitle className="font-nunito text-headline4 md:text-headline3 font-bold">
            Notifications
          </SheetTitle>
          {NotificationLayout()}
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}

function Sidebar({
  sidebarMenus,
  user,
  logo,
  walletBalance,
  Link,
  NavLink,
  useLocation,
  NotificationLayout = "",
  handleLogout = () => console.log("Logout......"),
  showWalletBalance = true,
}) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.shiftKey && event.key.toLowerCase() === "s") {
        const tag = event.target?.tagName?.toLowerCase();
        if (
          tag === "input" ||
          tag === "textarea" ||
          event.target?.isContentEditable
        ) {
          return;
        }
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const tooltipSide = isCollapsed ? "right" : "top";
  const toggleTooltip = isCollapsed
    ? "Expand sidebar (Shift+S)"
    : "Collapse sidebar (Shift+S)";

  const TernaryNavItem = React.forwardRef(
    ({ Icon, path, isCollapsed: collapsed, ...props }, ref) => (
      <NavLink
        ref={ref}
        to={path}
        {...props}
        className={({ isActive }) =>
          `${
            isActive
              ? "bg-primary/15 text-primary/95 border-primary/30"
              : "text-background/70 hover:bg-primary/10 border-background/30"
          } flex group items-center rounded-xl border-[2px] transition-colors ease-in-out duration-150 ${
            collapsed
              ? "w-10 h-10 justify-center p-0"
              : "flex-1 justify-center p-2.5"
          }`
        }
      >
        {Icon && (
          <Icon className="text-xl group-hover:text-primary/80 shrink-0" />
        )}
      </NavLink>
    ),
  );

  return (
    <TooltipProvider delayDuration={100}>
      <div
        className={`h-[calc(100%-8px)] ml-1 my-1 hidden lg:flex flex-col justify-between grow-0 shrink-0 bg-secondary text-background rounded-2xl overflow-x-hidden overflow-y-auto transition-[width] duration-300 ease-in-out ${
          isCollapsed ? "w-[64px] p-1.5 pt-3" : "w-56 p-2 pt-5"
        }`}
      >
        <div className="min-w-0">
          <div
            className={`flex items-center mb-4 min-w-0 ${
              isCollapsed ? "justify-center px-0" : "justify-between px-2"
            }`}
          >
            {!isCollapsed && (
              <Link to="/" className="cursor-pointer shrink-0">
                <img src={logo} alt="ICC" className="h-11" />
              </Link>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSidebar}
                  className="flex shrink-0 justify-center items-center w-8 h-8 rounded-lg text-background/70 hover:bg-background/10 hover:text-background transition-colors"
                  aria-label={toggleTooltip}
                >
                  {isCollapsed ? (
                    <LuPanelLeftOpen className="text-lg" />
                  ) : (
                    <LuPanelLeftClose className="text-lg" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side={tooltipSide}
                align="center"
                className="z-50"
                sideOffset={isCollapsed ? 8 : 4}
              >
                {toggleTooltip}
              </TooltipContent>
            </Tooltip>
          </div>

          <div
            className={`flex flex-col gap-[2px] my-2 min-w-0 px-1 ${isCollapsed ? "items-center" : ""}`}
          >
            {sidebarMenus.primary.map((item, index) => (
              <SidebarItem
                key={index}
                Icon={item.icon}
                title={item.title}
                path={item.path}
                NavLink={NavLink}
                useLocation={useLocation}
                isCollapsed={isCollapsed}
              />
            ))}
            {sidebarMenus.secondary.map((item, index) => (
              <SidebarItem
                key={index}
                Icon={item.icon}
                title={item.title}
                path={item.path}
                NavLink={NavLink}
                useLocation={useLocation}
                isCollapsed={isCollapsed}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-[6px] min-w-0 px-1">
          <div
            className={`flex min-w-0 ${
              isCollapsed ? "flex-col gap-2 items-center" : "gap-2"
            }`}
          >
            {sidebarMenus.ternary.map((item, index) =>
              item.title === "Notifications" ? (
                <NotificationTernaryItem
                  key={index}
                  Icon={item.icon}
                  title={item.title}
                  isCollapsed={isCollapsed}
                  NotificationLayout={NotificationLayout}
                  tooltipSide={tooltipSide}
                />
              ) : (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <TernaryNavItem
                      Icon={item.icon}
                      path={item.path}
                      isCollapsed={isCollapsed}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side={tooltipSide}
                    align="center"
                    className="z-50"
                    sideOffset={isCollapsed ? 8 : 4}
                  >
                    {item.title}
                  </TooltipContent>
                </Tooltip>
              ),
            )}
          </div>

          {isCollapsed ? (
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button className="flex my-1 justify-center items-center w-10 mx-auto h-10 rounded-lg bg-background/10 text-background font-semibold text-sm hover:bg-primary/20 transition-colors ring-1 ring-background/10">
                      {user.name && user.name !== "------" ? (
                        getInitials(user.name)
                      ) : (
                        <IoPerson className="text-lg" />
                      )}
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="center"
                  className="z-50"
                  sideOffset={8}
                >
                  {user.name || "Account"}
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                side="right"
                align="end"
                className="w-64 overflow-hidden bg-background_2 shadow-xl p-0.5 flex flex-col gap-0.5 rounded-2xl"
                sideOffset={16}
              >
                <div className="bg-secondary/95 px-4 py-3 flex items-center gap-3 rounded-t-xl rounded-b-sm">
                  <div className="flex justify-center items-center w-10 h-10 rounded-xl bg-primary/20 text-background font-semibold text-sm shrink-0">
                    {user.name && user.name !== "------" ? (
                      getInitials(user.name)
                    ) : (
                      <IoPerson className="text-xl" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-body2 leading-tight tracking-wide font-semibold text-background line-clamp-1">
                      {user.name}
                    </p>
                    {user.email && user.email !== "------" && (
                      <p className="text-xs leading-tight tracking-wide text-background/50 line-clamp-1">
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-1 flex flex-col gap-1 rounded-b-xl rounded-t-sm border border-secondary/15 bg-background">
                  <Link
                    to="/my-profile"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-secondary hover:bg-secondary/5 transition-colors"
                  >
                    <HiOutlineUserCircle className="text-xl shrink-0 text-secondary/70" />
                    <span className="text-body2 font-medium">My Profile</span>
                  </Link>

                  {showWalletBalance && (
                    <div className="mx-1 p-2 rounded-lg bg-secondary/5 border border-secondary/10">
                      <div className="flex items-center gap-2 mb-1">
                        <IoWallet className="text-secondary/60" />
                        <p className="text-xs font-semibold text-secondary/70 uppercase tracking-wide">
                          Wallet Balance
                        </p>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className="text-body1 font-semibold text-secondary flex-1">
                          <span className="font-nunito">₹</span>
                          {walletBalance?.toLocaleString?.() ??
                            walletBalance ??
                            0}
                        </p>
                        <Link to="/wallet">
                          <Button
                            size="xs"
                            variant="default"
                            className="opacity-95"
                          >
                            Recharge
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors w-full text-left mt-1"
                  >
                    <IoLogOut className="text-xl shrink-0" />
                    <span className="text-body2 font-medium">Logout</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <>
              {showWalletBalance && (
                <div className="flex flex-col bg-background text-secondary/90 p-2 rounded-xl gap-1">
                  <p className="text-body2 line-clamp-1 flex overflow-hidden grow-0 shrink-0 whitespace-nowrap font-semibold ml-1">
                    Wallet Balance
                  </p>
                  <div className="flex gap-1 items-center bg-secondary/10 p-1 pl-2 rounded-lg">
                    <p className="w-full text-body1 font-semibold ">
                      <span className="font-nunito">₹</span>
                      {walletBalance.toLocaleString()}
                    </p>
                    <Link to="/wallet">
                      <Button
                        size="xs"
                        variant="default"
                        className={"opacity-95"}
                      >
                        Recharge
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              <div className="flex bg-background/10 text-background/90 p-1 rounded-xl gap-1">
                <div className="group hover:bg-primary/20 hover:scale-[0.97] transition-all flex flex-col justify-center flex-1 line-clamp-1 rounded-lg gap-[2px] pl-2 pr-1 py-1 bg-background/5">
                  <Link to="/my-profile">
                    <p className="text-xs font-normal line-clamp-1 flex overflow-hidden grow-0 shrink-0 pl-[2px] opacity-60 whitespace-nowrap">
                      Logged in as
                    </p>
                    <p className="w-full line-clamp-1 flex text-body2 font-semibold grow-0 shrink-0 overflow-hidden whitespace-nowrap">
                      {user.name}
                    </p>
                  </Link>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleLogout}
                      className="group flex shrink-0 justify-center items-center w-10 h-full bg-background/5 rounded-lg hover:bg-primary/20 hover:scale-95"
                    >
                      <IoLogOut className="text-background/90 text-xl -mr-[2px] group-hover:text-primary/80 cursor-pointer" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="center"
                    className="z-50"
                    sideOffset={4}
                  >
                    Logout
                  </TooltipContent>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default Sidebar;
